import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../providers/AuthContext';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import Loader from '../shared/Loader';
import { showSuccess, showError } from '../../utils/sweetAlert';

const Orders = () => {
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [sendingEmailOrderId, setSendingEmailOrderId] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    enEspera: 0,
    enPreparacion: 0,
    listoParaRetiro: 0,
    enviados: 0,
    entregados: 0,
    cancelados: 0,
    totalRevenue: 0
  });

  useEffect(() => {    
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/orders`, {
          headers: getAdminAuthHeaders(token)
        });
        const ordersWithStatus = response.data.map(order => ({
          ...order,
          status: order.status || 'en espera'
        }));
        setOrders(ordersWithStatus);
        calculateStats(ordersWithStatus);
      } catch (err) {
        setError('Error al obtener los pedidos');
        showError('No se pudieron cargar los pedidos');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token]);

  const calculateStats = (ordersList) => {
    const newStats = {
      total: ordersList.length,
      enEspera: ordersList.filter(o => o.status === 'en espera').length,
      enPreparacion: ordersList.filter(o => o.status === 'en preparacion').length,
      listoParaRetiro: ordersList.filter(o => o.status === 'terminado').length,
      enviados: ordersList.filter(o => o.status === 'enviado').length,
      entregados: ordersList.filter(o => o.status === 'entregado').length,
      cancelados: ordersList.filter(o => o.status === 'cancelado').length,
      totalRevenue: ordersList
        .filter(o => o.payment?.status === 'approved')
        .reduce((sum, o) => sum + (o.total || 0), 0)
    };
    setStats(newStats);
  };

  const handleStatusChange = useCallback(async (orderId, newStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
        { status: newStatus },
        { headers: getAdminAuthHeaders(token) }
      );
      
      const updatedOrders = orders.map(order =>
        order._id === orderId ? { ...order, status: newStatus } : order
      );
      setOrders(updatedOrders);
      calculateStats(updatedOrders);
      showSuccess('Estado actualizado correctamente');
    } catch (err) {
      showError('Error al actualizar el estado');
    }
  }, [orders, token]);

  const handleTogglePayment = useCallback(async (order) => {
    const isApproved = order.payment?.status === 'approved';
    const newStatus = isApproved ? 'pending' : 'approved';
    try {
      const updateData = {
        'payment.status': newStatus,
        'payment.paidAt': newStatus === 'approved' ? new Date().toISOString() : null
      };
      await axios.put(
        `${process.env.REACT_APP_API_URL}/orders/${order._id}`,
        updateData,
        { headers: getAdminAuthHeaders(token) }
      );
      const updatedOrders = orders.map(o =>
        o._id === order._id
          ? { ...o, payment: { ...o.payment, status: newStatus, paidAt: newStatus === 'approved' ? new Date().toISOString() : null } }
          : o
      );
      setOrders(updatedOrders);
      calculateStats(updatedOrders);
      showSuccess(newStatus === 'approved' ? 'Pedido marcado como pagado' : 'Pago desmarcado');
    } catch (err) {
      showError('Error al actualizar el pago');
    }
  }, [orders, token]);

  const getStatusEmailType = (order) => {
    if (order?.status === 'terminado') return 'readyForPickup';
    if (order?.status === 'enviado') return 'shipped';
    return null;
  };

  const handleSendStatusEmail = useCallback(async (order) => {
    if (!order?._id) return;

    const type = getStatusEmailType(order);
    if (!type) {
      showError('Solo se puede reenviar cuando el pedido está en "Listo para Retiro" o "Enviado"');
      return;
    }

    try {
      setSendingEmailOrderId(order._id);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/email/send-status-notification/${order._id}`,
        { type },
        { headers: getAdminAuthHeaders(token) }
      );

      if (response.data?.success) {
        showSuccess(`Email enviado a ${order.buyerInfo?.mail || 'la clienta'}`);
      } else {
        showError(response.data?.error || 'No se pudo enviar el email');
      }
    } catch (err) {
      showError(err.response?.data?.error || 'Error al enviar el email');
    } finally {
      setSendingEmailOrderId(null);
    }
  }, [token]);

  // Función para asignar un color de fondo según el estado
  const getBackgroundColor = (status) => {
    switch(status) {
      case 'en espera':
        return '#f8f9fa'; // gris claro
      case 'en preparacion':
        return '#fff3cd'; // amarillo claro
      case 'terminado':
        return '#d4edda'; // verde claro
      case 'enviado':
        return '#cce5ff'; // azul claro
      case 'entregado':
        return '#d1e7dd'; // verde más oscuro
      case 'cancelado':
        return '#f8d7da'; // rojo claro
      default:
        return '#ffffff';
    }
  };

  // Función para obtener el icono del tipo de envío
  const getShippingIcon = (shippingType) => {
    switch(shippingType) {
      case 'dac_montevideo':
      case 'envio_montevideo':
        return 'fa-motorcycle';
      case 'retiro_local':
      case 'retiro_blandengues':
      case 'retiro_ferrer_serra':
        return 'fa-store';
      case 'envio_interior':
        return 'fa-truck';
      default:
        return 'fa-box';
    }
  };

  // Función para obtener el badge del tipo de envío
  const getShippingBadge = (shipping) => {
    if (!shipping) return null;
    
    const colors = {
      'dac_montevideo': 'bg-info',
      'envio_montevideo': 'bg-info',
      'retiro_local': 'bg-success',
      'retiro_blandengues': 'bg-success',
      'retiro_ferrer_serra': 'bg-success',
      'envio_interior': 'bg-warning text-dark'
    };
    
    return (
      <span className={`badge ${colors[shipping.type] || 'bg-secondary'}`}>
        <i className={`fa-solid ${getShippingIcon(shipping.type)} me-1`}></i>
        {shipping.label}
        {shipping.cost > 0 && ` ($${shipping.cost})`}
      </span>
    );
  };

  const handlePrintOrder = (order) => {
    const totalQty = order.items.reduce((acc, item) => acc + item.quantity, 0);
    const dateStr = new Date(order.date).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const isPickup = ['retiro_local', 'retiro_blandengues', 'retiro_ferrer_serra'].includes(order.shipping?.type);

    const itemsHtml = order.items.map(item => {
      const variants = [
        item.variant?.size,
        item.variant?.color,
        ...(item.selectedSizes || []),
        ...(item.selectedColors || [])
      ].filter(Boolean);
      if (!item.variant?.size && !item.variant?.color && item.variantInfo) variants.push(item.variantInfo);
      return `
        <tr>
          <td style="padding:10px 8px;border-bottom:1px solid #f0f0f0;">
            <strong>${item.quantity}×</strong> ${item.name}
            ${variants.length ? `<br/><span style="font-size:11px;color:#666;">${variants.join(' · ')}</span>` : ''}
            ${item.variant?.sku ? `<br/><span style="font-size:10px;color:#bbb;">${item.variant.sku}</span>` : ''}
          </td>
          <td style="padding:10px 8px;border-bottom:1px solid #f0f0f0;text-align:right;white-space:nowrap;">$${(item.price * item.quantity).toLocaleString('es-UY')}</td>
        </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>Pedido #${order.orderNumber || order._id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 13px; color: #1a1a1a; padding: 32px; max-width: 680px; margin: auto; }
    h1 { font-size: 20px; margin-bottom: 4px; }
    .meta { color: #666; font-size: 12px; margin-bottom: 24px; }
    .section { margin-bottom: 20px; }
    .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #888; border-bottom: 1px solid #e0e0e0; padding-bottom: 6px; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; }
    td { vertical-align: top; }
    .totals td { padding: 4px 8px; }
    .totals .total-row td { font-weight: 700; font-size: 15px; padding-top: 8px; border-top: 2px solid #1a1a1a; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
    .label { color: #888; font-size: 11px; margin-bottom: 2px; }
    .value { font-weight: 600; }
    @media print { body { padding: 16px; } }
  </style>
</head>
<body>
  <h1>Pedido #${order.orderNumber || order._id}</h1>
  <p class="meta">${dateStr} &nbsp;·&nbsp; ${totalQty} artículo${totalQty !== 1 ? 's' : ''} &nbsp;·&nbsp; Estado: ${order.status || 'en espera'}</p>

  <div class="info-grid" style="margin-bottom:24px;">
    <div class="section">
      <div class="section-title">Cliente</div>
      <div class="value">${order.buyerInfo?.nombre || '-'}</div>
      ${order.buyerInfo?.mail ? `<div>${order.buyerInfo.mail}</div>` : ''}
      ${order.buyerInfo?.telefono ? `<div>${order.buyerInfo.telefono}</div>` : ''}
      ${order.buyerInfo?.cedula ? `<div style="color:#888;font-size:11px;">CI: ${order.buyerInfo.cedula}</div>` : ''}
    </div>
    <div class="section">
      <div class="section-title">Envío y Pago</div>
      <div class="value">${order.shipping?.label || '-'}</div>
      ${!isPickup && order.shippingAddress?.direccion ? `<div style="margin-top:4px;">${order.shippingAddress.direccion}, ${order.shippingAddress.ciudad || ''}</div>` : ''}
      ${order.paymentMethodInfo?.label ? `<div style="margin-top:8px;color:#888;">Pago: ${order.paymentMethodInfo.label}</div>` : ''}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Productos (${totalQty} artículos)</div>
    <table>
      <tbody>${itemsHtml}</tbody>
    </table>
    <table class="totals" style="margin-top:12px;">
      <tbody>
        ${order.subtotal != null ? `<tr><td>Subtotal</td><td style="text-align:right;">$${Number(order.subtotal).toLocaleString('es-UY')}</td></tr>` : ''}
        <tr><td>Envío</td><td style="text-align:right;color:${(order.shipping?.cost || 0) === 0 ? '#1f8e38' : '#1a1a1a'}">${(order.shipping?.cost || 0) === 0 ? 'Gratis' : `$${order.shipping.cost}`}</td></tr>
        <tr class="total-row"><td>Total</td><td style="text-align:right;">$${Number(order.total).toLocaleString('es-UY')}</td></tr>
      </tbody>
    </table>
  </div>

  ${order.notes ? `<div class="section"><div class="section-title">Notas</div><div>${order.notes}</div></div>` : ''}

  <script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/orders`, {
        headers: getAdminAuthHeaders(token)
      });
      const ordersWithStatus = response.data.map(order => ({
        ...order,
        status: order.status || 'en espera'
      }));
      setOrders(ordersWithStatus);
      calculateStats(ordersWithStatus);
    } catch (err) {
      showError('No se pudieron cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar pedidos por fecha, estado y búsqueda
  const filteredOrders = useMemo(() => orders.filter(order => {
    const orderDate = new Date(order.date);
    const now = new Date();

    // Date filter
    let matchesDate = true;
    if (dateFilter === 'today') {
      matchesDate = orderDate.toDateString() === now.toDateString();
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      matchesDate = orderDate >= weekAgo;
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      matchesDate = orderDate >= monthAgo;
    } else if (dateFilter === 'custom') {
      if (dateFrom) {
        const from = new Date(dateFrom + 'T00:00:00');
        matchesDate = orderDate >= from;
      }
      if (dateTo && matchesDate) {
        const to = new Date(dateTo + 'T23:59:59');
        matchesDate = orderDate <= to;
      }
    }

    // Status filter
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    // Search filter
    let matchesSearch = true;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      matchesSearch = 
        String(order.orderNumber || '').toLowerCase().includes(q) ||
        (order.buyerInfo?.nombre || '').toLowerCase().includes(q) ||
        (order.buyerInfo?.mail || '').toLowerCase().includes(q);
    }

    return matchesDate && matchesStatus && matchesSearch;
  }), [orders, dateFilter, dateFrom, dateTo, statusFilter, searchQuery]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'en espera': { color: '#6c757d', icon: 'fa-clock', text: 'En Espera' },
      'en preparacion': { color: '#ffc107', icon: 'fa-box-open', text: 'Preparando' },
      'terminado': { color: '#1f8e38', icon: 'fa-check', text: 'Listo' },
      'enviado': { color: '#17a2b8', icon: 'fa-truck', text: 'Enviado' },
      'entregado': { color: '#1f8e38', icon: 'fa-check-double', text: 'Entregado' },
      'cancelado': { color: '#dc3545', icon: 'fa-times', text: 'Cancelado' }
    };
    
    const config = statusConfig[status] || statusConfig['en espera'];
    
    return (
      <span className="badge" style={{ backgroundColor: config.color, fontSize: '0.85rem', padding: '6px 12px' }}>
        <i className={`fa-solid ${config.icon} me-1`}></i>{config.text}
      </span>
    );
  };

  const getEmailNotificationMeta = (order) => {
    const n = order.notifications || {};

    if (n.shippedEmailSentAt) {
      return {
        label: 'Enviado (en camino)',
        icon: 'fa-envelope-circle-check',
        background: '#e8f7ee',
        color: '#1f8e38',
        detail: new Date(n.shippedEmailSentAt).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })
      };
    }

    if (n.readyForPickupEmailSentAt) {
      return {
        label: 'Enviado (listo para retirar)',
        icon: 'fa-envelope-circle-check',
        background: '#e8f7ee',
        color: '#1f8e38',
        detail: new Date(n.readyForPickupEmailSentAt).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })
      };
    }

    if (n.shippedEmailError || n.readyForPickupEmailError) {
      return {
        label: 'Email con error',
        icon: 'fa-envelope-circle-xmark',
        background: '#fdecec',
        color: '#c62828',
        detail: n.shippedEmailError || n.readyForPickupEmailError
      };
    }

    if (n.orderConfirmationEmailSentAt) {
      return {
        label: 'Confirmación enviada',
        icon: 'fa-envelope-circle-check',
        background: '#e8f7ee',
        color: '#1f8e38',
        detail: new Date(n.orderConfirmationEmailSentAt).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })
      };
    }

    return {
      label: 'Email pendiente',
      icon: 'fa-envelope',
      background: '#f3f4f6',
      color: '#6b7280',
      detail: 'No se envió email de estado todavía'
    };
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-center mt-5 text-danger">{error}</p>;

  const getOrderStatusBadgeClass = (status) => {
    const map = {
      'en espera': 'badge-secondary',
      'en preparacion': 'badge-warning',
      'terminado': 'badge-success',
      'enviado': 'badge-info',
      'entregado': 'badge-success',
      'cancelado': 'badge-danger'
    };
    return map[status] || 'badge-secondary';
  };

  const getOrderStatusText = (status) => {
    const map = {
      'en espera': 'En Espera',
      'en preparacion': 'En Preparación',
      'terminado': 'Listo para Retiro',
      'enviado': 'Enviado',
      'entregado': 'Entregado',
      'cancelado': 'Cancelado'
    };
    return map[status] || status;
  };

  const statusPills = [
    { key: 'all', label: '', count: orders.length, dot: null },
    { key: 'en espera', label: 'En Espera', count: stats.enEspera, dot: '#6c757d' },
    { key: 'en preparacion', label: 'En Preparación', count: stats.enPreparacion, dot: '#ffc107' },
    { key: 'terminado', label: 'Listo para Retiro', count: stats.listoParaRetiro, dot: '#1f8e38' },
    { key: 'enviado', label: 'Enviado', count: stats.enviados, dot: '#17a2b8' },
    { key: 'entregado', label: 'Entregado', count: stats.entregados, dot: '#1f8e38' },
    { key: 'cancelado', label: 'Cancelado', count: stats.cancelados, dot: '#dc3545' }
  ];

  return (
    <div className="orders-page">
      {/* Header */}
      <div className="admin-page-header">
        <div className="header-left">
          <h1>Pedidos Realizados</h1>
          <p>Gestiona y visualiza todos los pedidos de tu tienda</p>
        </div>
        <div className="header-right">
          <button className="btn btn-dark" onClick={handleRefresh} disabled={loading}>
            <i className="fas fa-rotate-right me-2"></i> Actualizar
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="admin-stat-row">
        <div className="admin-stat-card">
          <div className="admin-stat-icon blue">
            <i className="fas fa-shopping-cart"></i>
          </div>
          <div className="admin-stat-info">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total Pedidos</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon orange">
            <i className="fas fa-clock"></i>
          </div>
          <div className="admin-stat-info">
            <span className="stat-number">{stats.enEspera}</span>
            <span className="stat-label">Pendientes</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon red">
            <i className="fas fa-box-open"></i>
          </div>
          <div className="admin-stat-info">
            <span className="stat-number">{stats.enPreparacion}</span>
            <span className="stat-label">En Preparación</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon green">
            <i className="fas fa-dollar-sign"></i>
          </div>
          <div className="admin-stat-info">
            <span className="stat-number">${stats.totalRevenue.toLocaleString('es-AR')}</span>
            <span className="stat-label">Ingresos (Pagados)</span>
          </div>
        </div>
      </div>

      {/* Filter Bar: Search + Date Buttons + Date Range */}
      <div className="admin-filter-bar" style={{ flexWrap: 'wrap' }}>
        <div className="filter-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Buscar por N° pedido, nombre, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="admin-date-filters">
          <button className={`date-filter-btn ${dateFilter === 'today' ? 'active' : ''}`} onClick={() => setDateFilter('today')}>Hoy</button>
          <button className={`date-filter-btn ${dateFilter === 'week' ? 'active' : ''}`} onClick={() => setDateFilter('week')}>Última Semana</button>
          <button className={`date-filter-btn ${dateFilter === 'month' ? 'active' : ''}`} onClick={() => setDateFilter('month')}>Último Mes</button>
          <button className={`date-filter-btn ${dateFilter === 'all' ? 'active' : ''}`} onClick={() => setDateFilter('all')}>Todos</button>
          <input
            type="date"
            className="admin-date-input"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setDateFilter('custom'); }}
          />
          <span className="date-range-separator">—</span>
          <input
            type="date"
            className="admin-date-input"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setDateFilter('custom'); }}
          />
        </div>
      </div>

      {/* Status Pills */}
      <div className="admin-status-pills">
        {statusPills.map(pill => (
          <button
            key={pill.key}
            className={`status-pill ${statusFilter === pill.key ? 'active' : ''}`}
            onClick={() => setStatusFilter(pill.key)}
          >
            {pill.dot && <span className="pill-dot" style={{ background: pill.dot }}></span>}
            {pill.key === 'all' ? '' : pill.label}
            <span className="pill-count">{pill.count}</span>
          </button>
        ))}
      </div>

      {/* Order count */}
      <div style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '16px' }}>
        {filteredOrders.length} pedido{filteredOrders.length !== 1 ? 's' : ''}
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="admin-empty-state">
          <div className="empty-icon">
            <i className="fas fa-inbox"></i>
          </div>
          <h4>No hay pedidos</h4>
          <p>No se encontraron pedidos con los filtros actuales</p>
        </div>
      ) : (
        <div>
          {filteredOrders.map(order => {
            const emailMeta = getEmailNotificationMeta(order);

            return (
            <div key={order._id} className="admin-order-card">
              <div className="order-main" onClick={() => setExpandedOrder(expandedOrder === order._id ? null : order._id)}>
                <div className="order-left">
                  <span className="order-number">#{order.orderNumber || '—'}</span>
                  <div className="order-badges">
                    <span className={`order-badge ${getOrderStatusBadgeClass(order.status)}`}>
                      {getOrderStatusText(order.status)}
                    </span>
                    {order.payment?.status === 'approved' ? (
                      <span className="order-badge badge-paid">
                        <i className="fas fa-check me-1"></i>Pagado
                      </span>
                    ) : (
                      <span className="order-badge badge-unpaid">
                        <i className="fas fa-times me-1"></i>Pago Pendiente
                      </span>
                    )}
                    {emailMeta.label !== 'Email pendiente' && (
                      <span
                        className="order-badge"
                        style={{
                          backgroundColor: emailMeta.background,
                          color: emailMeta.color,
                          border: `1px solid ${emailMeta.color}22`
                        }}
                        title={emailMeta.detail}
                      >
                        <i className={`fas ${emailMeta.icon} me-1`}></i>{emailMeta.label}
                      </span>
                    )}
                  </div>
                  <div className="order-meta">
                    <span><i className="fas fa-calendar"></i>{new Date(order.date).toLocaleDateString('es-UY')}</span>
                    <span><i className="fas fa-clock"></i>{new Date(order.date).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}</span>
                    {order.buyerInfo?.nombre && (
                      <span><i className="fas fa-user"></i>{order.buyerInfo.nombre}</span>
                    )}
                  </div>
                </div>
                <div className="order-right">
                  <span className="order-amount">${order.total}</span>
                  <button className="order-expand" onClick={(e) => { e.stopPropagation(); setExpandedOrder(expandedOrder === order._id ? null : order._id); }}>
                    <i className={`fas fa-chevron-${expandedOrder === order._id ? 'up' : 'down'}`}></i>
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedOrder === order._id && (
                <div style={{ padding: '0 4px 4px', borderTop: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, padding: '16px 0 4px' }}>

                    {/* ── Columna 1: Productos ── */}
                    <div style={{ background: '#fafafa', border: '1px solid #efefef', borderRadius: 8, padding: '16px 16px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
                        <span style={{ width: 3, height: 14, background: '#6c757d', borderRadius: 2, display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555' }}>
                          Productos
                        </span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#aaa' }}>{order.items.reduce((acc, item) => acc + item.quantity, 0)} ítem{order.items.reduce((acc, item) => acc + item.quantity, 0) !== 1 ? 's' : ''}</span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {order.items.map((item, idx) => {
                          const imgSrc = item.variant?.images?.[0] || item.images?.[0] || item.image;
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, paddingBottom: idx < order.items.length - 1 ? 10 : 0, borderBottom: idx < order.items.length - 1 ? '1px solid #ebebeb' : 'none' }}>
                              {imgSrc ? (
                                <img src={getDisplayImageUrl(imgSrc)} alt={item.name}
                                  style={{ width: 48, height: 48, objectFit: 'cover', flexShrink: 0, border: '1px solid #e0e0e0', borderRadius: 4 }} />
                              ) : (
                                <div style={{ width: 48, height: 48, background: '#ebebeb', flexShrink: 0, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <i className="fas fa-shirt" style={{ color: '#bbb', fontSize: '1rem' }}></i>
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.84rem', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3 }}>
                                  <span style={{ color: '#1a1a1a', fontWeight: 700, marginRight: 3 }}>{item.quantity}×</span>{item.name}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                                  {item.variant?.size && <span style={{ fontSize: '0.66rem', background: '#e9ecef', color: '#555', padding: '1px 6px', borderRadius: 3 }}>{item.variant.size}</span>}
                                  {item.variant?.color && <span style={{ fontSize: '0.66rem', background: '#e9ecef', color: '#555', padding: '1px 6px', borderRadius: 3 }}>{item.variant.color}</span>}
                                  {!item.variant?.size && !item.variant?.color && item.variantInfo && (
                                    <span style={{ fontSize: '0.66rem', background: '#e9ecef', color: '#555', padding: '1px 6px', borderRadius: 3 }}>{item.variantInfo}</span>
                                  )}
                                  {item.selectedSizes?.map((s, i) => <span key={i} style={{ fontSize: '0.66rem', border: '1px solid #ccc', color: '#555', padding: '1px 6px', borderRadius: 3 }}>{s}</span>)}
                                  {item.selectedColors?.map((c, i) => <span key={i} style={{ fontSize: '0.66rem', border: '1px solid #ccc', color: '#555', padding: '1px 6px', borderRadius: 3 }}>{c}</span>)}
                                </div>
                                {item.variant?.sku && (
                                  <div style={{ fontSize: '0.66rem', color: '#bbb', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }} title={item.variant.sku}>
                                    {item.variant.sku}
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a1a', flexShrink: 0 }}>
                                ${(item.price * item.quantity).toLocaleString('es-UY')}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed #e0e0e0' }}>
                        {order.subtotal != null && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#888', marginBottom: 4 }}>
                            <span>Subtotal</span><span>${Number(order.subtotal).toLocaleString('es-UY')}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#888', marginBottom: 6 }}>
                          <span>Envío</span>
                          <span style={{ color: (order.shipping?.cost || 0) === 0 ? '#1f8e38' : '#888' }}>
                            {(order.shipping?.cost || 0) === 0 ? 'Gratis' : `$${order.shipping.cost}`}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92rem', fontWeight: 700, color: '#1a1a1a' }}>
                          <span>Total</span><span>${Number(order.total).toLocaleString('es-UY')}</span>
                        </div>
                      </div>

                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #efefef' }}>
                        <button
                          onClick={() => handlePrintOrder(order)}
                          style={{ width: '100%', fontSize: '0.76rem', fontWeight: 600, padding: '8px 12px', background: '#fff', border: '1.5px solid #1a1a1a', borderRadius: 6, color: '#1a1a1a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                        >
                          <i className="fas fa-file-pdf"></i>Generar PDF / Imprimir
                        </button>
                      </div>
                    </div>

                    {/* ── Columna 2: Cliente / Envío / Pago ── */}
                    <div style={{ background: '#fafafa', border: '1px solid #efefef', borderRadius: 8, padding: '16px 16px 12px' }}>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
                        <span style={{ width: 3, height: 14, background: '#4a90d9', borderRadius: 2, display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555' }}>Cliente</span>
                      </div>
                      {order.buyerInfo && (
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a1a1a', marginBottom: 6 }}>{order.buyerInfo.nombre}</div>
                          {order.buyerInfo.mail && (
                            <div style={{ fontSize: '0.8rem', color: '#777', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <i className="fas fa-envelope" style={{ color: '#bbb', width: 12 }}></i>{order.buyerInfo.mail}
                            </div>
                          )}
                          {order.buyerInfo.telefono && (
                            <div style={{ fontSize: '0.8rem', color: '#777', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <i className="fas fa-phone" style={{ color: '#bbb', width: 12 }}></i>{order.buyerInfo.telefono}
                            </div>
                          )}
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
                        <span style={{ width: 3, height: 14, background: '#f59e0b', borderRadius: 2, display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555' }}>Envío</span>
                      </div>
                      {order.shipping?.label && (
                        <div style={{ fontSize: '0.82rem', color: '#444', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 7 }}>
                          <i className={`fa-solid ${getShippingIcon(order.shipping.type)}`} style={{ color: '#bbb', width: 14 }}></i>
                          <span>{order.shipping.label}</span>
                          {order.shipping.cost > 0 && <span style={{ color: '#aaa', fontSize: '0.76rem' }}>(${order.shipping.cost})</span>}
                        </div>
                      )}
                      {(() => {
                        const isPickup = ['retiro_local', 'retiro_blandengues', 'retiro_ferrer_serra'].includes(order.shipping?.type);
                        return !isPickup && order.shippingAddress?.direccion ? (
                          <div style={{ fontSize: '0.8rem', color: '#666', paddingLeft: 20 }}>
                            <div><i className="fas fa-location-dot me-1" style={{ color: '#bbb' }}></i>{order.shippingAddress.direccion}</div>
                            <div style={{ color: '#aaa' }}>{order.shippingAddress.ciudad}{order.shippingAddress.departamento && `, ${order.shippingAddress.departamento}`}</div>
                            {order.shippingAddress.referencias && <div style={{ color: '#aaa', marginTop: 2 }}>{order.shippingAddress.referencias}</div>}
                          </div>
                        ) : null;
                      })()}

                      {order.paymentMethodInfo?.label && (
                        <div style={{ marginTop: 14 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                            <span style={{ width: 3, height: 14, background: '#8b5cf6', borderRadius: 2, display: 'inline-block' }}></span>
                            <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555' }}>Forma de Pago</span>
                          </div>
                          <div style={{ fontSize: '0.82rem', color: '#444', display: 'flex', alignItems: 'center', gap: 7 }}>
                            <i className="fas fa-wallet" style={{ color: '#bbb', width: 14 }}></i>{order.paymentMethodInfo.label}
                          </div>
                        </div>
                      )}

                      {order.notes && (
                        <div style={{ marginTop: 14, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, fontSize: '0.8rem', color: '#92400e' }}>
                          <i className="fas fa-comment-dots me-2" style={{ color: '#f59e0b' }}></i>{order.notes}
                        </div>
                      )}
                    </div>

                    {/* ── Columna 3: Acciones ── */}
                    <div style={{ background: '#fafafa', border: '1px solid #efefef', borderRadius: 8, padding: '16px 16px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14 }}>
                        <span style={{ width: 3, height: 14, background: '#1a1a1a', borderRadius: 2, display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555' }}>Estado del Pedido</span>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 16 }}>
                        {[
                          { key: 'en espera', color: '#6c757d' },
                          { key: 'en preparacion', color: '#f59e0b' },
                          { key: 'terminado', color: '#1f8e38' },
                          { key: 'enviado', color: '#17a2b8' },
                          { key: 'entregado', color: '#1f8e38' },
                          { key: 'cancelado', color: '#dc3545' }
                        ].map(({ key, color }) => {
                          const isActive = order.status === key;
                          return (
                            <button
                              key={key}
                              onClick={() => handleStatusChange(order._id, key)}
                              style={{
                                fontSize: '0.74rem',
                                fontWeight: isActive ? 700 : 500,
                                padding: '6px 8px',
                                border: `1.5px solid ${isActive ? color : '#e0e0e0'}`,
                                borderRadius: 6,
                                background: isActive ? color : '#fff',
                                color: isActive ? '#fff' : '#555',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                textAlign: 'center'
                              }}
                            >
                              {getOrderStatusText(key)}
                            </button>
                          );
                        })}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
                        <span style={{ width: 3, height: 14, background: '#1f8e38', borderRadius: 2, display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555' }}>Pago</span>
                      </div>
                      <div style={{ marginBottom: 14 }}>
                        {order.payment?.status === 'approved' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#e8f7ee', border: '1px solid #a3d9b1', borderRadius: 6 }}>
                            <i className="fas fa-circle-check" style={{ color: '#1f8e38', fontSize: '0.9rem' }}></i>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1f8e38' }}>Pagado</div>
                              {order.payment.paidAt && (
                                <div style={{ fontSize: '0.7rem', color: '#4caf50' }}>
                                  {new Date(order.payment.paidAt).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                  {' '}{new Date(order.payment.paidAt).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}
                            </div>
                            <button onClick={() => handleTogglePayment(order)}
                              style={{ fontSize: '0.68rem', padding: '2px 7px', background: 'transparent', border: '1px solid #a3d9b1', borderRadius: 4, color: '#1f8e38', cursor: 'pointer' }}>
                              Desmarcar
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => handleTogglePayment(order)}
                            style={{ width: '100%', fontSize: '0.78rem', fontWeight: 600, padding: '8px 12px', background: '#fff', border: '1.5px dashed #a3d9b1', borderRadius: 6, color: '#1f8e38', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                            <i className="fas fa-circle-check"></i>Marcar como Pagado
                          </button>
                        )}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
                        <span style={{ width: 3, height: 14, background: '#4a90d9', borderRadius: 2, display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#555' }}>Confirmación Email</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleSendStatusEmail(order)}
                          disabled={sendingEmailOrderId === order._id || !order.buyerInfo?.mail || !getStatusEmailType(order)}
                          title={!getStatusEmailType(order) ? 'Solo disponible en estados Listo para Retiro o Enviado' : undefined}
                          style={{ fontSize: '0.76rem', fontWeight: 600, padding: '6px 12px', background: '#fff', border: '1.5px solid #333', borderRadius: 6, color: '#333', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, opacity: (!order.buyerInfo?.mail) ? 0.5 : 1 }}
                        >
                          {sendingEmailOrderId === order._id
                            ? <><i className="fas fa-spinner fa-spin"></i>Enviando...</>
                            : <><i className="fas fa-paper-plane"></i>Reenviar</>}
                        </button>
                        <span style={{ fontSize: '0.7rem', color: emailMeta.color, background: emailMeta.background, border: `1px solid ${emailMeta.color}33`, borderRadius: 4, padding: '3px 8px' }} title={emailMeta.detail}>
                          <i className={`fas ${emailMeta.icon} me-1`}></i>{emailMeta.label}
                        </span>
                      </div>
                      <p style={{ fontSize: '0.68rem', color: '#bbb', marginTop: 6, marginBottom: 0 }}>
                        Automático al pasar a "Listo para Retiro" o "Enviado".
                      </p>

                    </div>

                  </div>
                </div>
              )}
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
};

export default Orders;
